import { create } from "zustand";
import { EMPTY_ROWS, type TaskizeRows } from "~/features/taskize/rows";

/**
 * The tray's state.
 *
 * It is a store rather than scene state because **three surfaces open the same tray** — the ✨
 * selection menu, the document ⋯ menu and the Ask bar — and because the Ask bar is
 * shell-level: it cannot reach into a scene's `useState`.
 *
 * But the tray itself is scene-owned: it mounts inside `DocumentScene` and unmounts when you
 * leave the document. So the bar never opens it directly. It sets `pending`, navigates if it
 * has to, and the scene consumes `pending` in an effect and clears it (§C1.2, §E2 R8). kms got
 * away with a global mount because MobX stores outlive routes; slate's shape is honest about
 * the fact that a tray over no document is a tray over nothing.
 */
export interface TaskizeTrayState {
  open: boolean;
  documentId: string | null;
  /** The ✨ menu sends a selection; the ⋯ menu and the bar send the whole document. */
  selectedText: string | null;
  rows: TaskizeRows;
  /** Set once the extraction lands, for the header's provenance line. */
  thinkerName: string | null;
  /** A request from the shell that the document scene has not picked up yet. */
  pending: { documentId: string; selectedText?: string } | null;

  requestTaskize: (documentId: string, selectedText?: string) => void;
  takePending: () => void;
  openTray: (documentId: string, selectedText?: string) => void;
  closeTray: () => void;
  setRows: (rows: TaskizeRows, thinkerName: string | null) => void;
  patchRow: (key: string, patch: Record<string, unknown>) => void;
  setChecked: (key: string, checked: boolean) => void;
  /** Confirmed rows leave the tray; the tray closes itself when the last one does. */
  removeRows: (keys: string[]) => void;
}

function mapRows(rows: TaskizeRows, key: string, patch: Record<string, unknown>): TaskizeRows {
  const apply = <T extends { key: string }>(list: T[]): T[] =>
    list.map((row) => (row.key === key ? { ...row, ...patch } : row));
  return {
    tasks: apply(rows.tasks),
    decisions: apply(rows.decisions),
    reminders: apply(rows.reminders),
  };
}

export const useTaskizeTray = create<TaskizeTrayState>()((set, get) => ({
  open: false,
  documentId: null,
  selectedText: null,
  rows: EMPTY_ROWS,
  thinkerName: null,
  pending: null,

  requestTaskize: (documentId, selectedText) =>
    set({
      pending: selectedText === undefined ? { documentId } : { documentId, selectedText },
    }),

  takePending: () => {
    const { pending } = get();
    if (!pending) {
      return;
    }
    set({
      pending: null,
      open: true,
      documentId: pending.documentId,
      selectedText: pending.selectedText ?? null,
      rows: EMPTY_ROWS,
      thinkerName: null,
    });
  },

  openTray: (documentId, selectedText) =>
    set({
      open: true,
      documentId,
      selectedText: selectedText ?? null,
      rows: EMPTY_ROWS,
      thinkerName: null,
      pending: null,
    }),

  closeTray: () =>
    set({ open: false, rows: EMPTY_ROWS, selectedText: null, thinkerName: null, pending: null }),

  setRows: (rows, thinkerName) => set({ rows, thinkerName }),

  patchRow: (key, patch) => set((state) => ({ rows: mapRows(state.rows, key, patch) })),

  setChecked: (key, checked) => set((state) => ({ rows: mapRows(state.rows, key, { checked }) })),

  removeRows: (keys) =>
    set((state) => {
      const gone = new Set(keys);
      const rows: TaskizeRows = {
        tasks: state.rows.tasks.filter((row) => !gone.has(row.key)),
        decisions: state.rows.decisions.filter((row) => !gone.has(row.key)),
        reminders: state.rows.reminders.filter((row) => !gone.has(row.key)),
      };
      const remaining = rows.tasks.length + rows.decisions.length + rows.reminders.length;
      return remaining === 0
        ? { rows, open: false, selectedText: null, thinkerName: null }
        : { rows };
    }),
}));
